<template>
  <div>
    <h3 class="tracking-wide md:text-[22px] md:text-xl">Contact Information</h3>
    <div class="mb-5 flex justify-end md:mb-4">
      <p class="text-sm">* Required</p>
    </div>
    <form class="flex flex-col gap-y-4 font-Spectral">
      <div class="grid gap-3">
        <div class="flex justify-between gap-[2%]">
          <WrappedTextInput
            v-model="form.step2.firstName"
            aria-label="firstName"
            autocomplete="given-name"
            class="w-full"
            label="First name"
            placeholder="Taro"
            :error="errorMessages.firstName"
            :lang="'en'"
          />
          <WrappedTextInput
            v-model="form.step2.lastName"
            aria-label="lastName"
            autocomplete="family-name"
            class="w-full"
            label="Last name"
            placeholder="Wabunka"
            :error="errorMessages.lastName"
            :lang="'en'"
          />
        </div>
      </div>
      <WrappedTextInput v-model="form.step2.email" aria-label="email" autocomplete="email" label="Email" placeholder="wabunka.taro@j-cat.jp" type="email" :error="errorMessages.email" :lang="'en'" />
      <!-- autocomplete 明示は必須: name/autocomplete が無いと Chrome のクラウドソース型予測が順位ベースで適用され、会社名欄に電話番号がオートフィルされる誤爆が発生する（5/21 の欄追加で実際に発生） -->
      <WrappedTextInput
        v-model="form.step2.companyName"
        aria-label="companyName"
        autocomplete="organization"
        label="Company name (If you are booking on behalf of clients)"
        placeholder="Wa Travel Agency, J-CAT, Inc."
        :lang="'en'"
        :required="false"
      />

      <!-- Who is this booking for? -->
      <FormInputWrapper label="Who is this booking for?" :lang="'en'" :required="false">
        <div class="border border-solid border-light-bd-muted">
          <!-- For myself -->
          <div class="flex flex-col gap-4 border-b border-solid border-light-bd-muted p-4">
            <label class="flex cursor-pointer items-center gap-2">
              <input class="sr-only" name="bookingFor" type="radio" value="myself" :checked="!form.step2.shouldShowSomeoneElseForm" @input="form.step2.shouldShowSomeoneElseForm = false" />
              <span
                class="box-border inline-block h-4 w-4 min-w-4 rounded-full border border-solid"
                :class="[form.step2.shouldShowSomeoneElseForm ? 'border-gray2' : 'border-black1', !form.step2.shouldShowSomeoneElseForm ? 'bg-black1' : 'bg-white']"
              />
              <span :class="!form.step2.shouldShowSomeoneElseForm ? 'font-semibold' : ''">For myself</span>
            </label>
            <template v-if="!form.step2.shouldShowSomeoneElseForm">
              <div class="flex flex-col gap-4 pl-6">
                <WrappedTextInput
                  v-model="form.step2.forMyselfPhoneNumber"
                  aria-label="forMyselfPhoneNumber"
                  autocomplete="tel"
                  label="Phone number  (WhatsApp preferred)"
                  placeholder="+81 90-1234-5678"
                  type="tel"
                  :error="errorMessages.forMyselfPhoneNumber"
                  :lang="'en'"
                />
                <MoleculesWabunkaWrappedComboBox
                  v-model="form.step2.forMyselfNationality"
                  aria-label="forMyselfNationality"
                  label="Nationality"
                  :error="errorMessages.forMyselfNationality"
                  :lang="'en'"
                  :options="options"
                />
              </div>
            </template>
          </div>
          <!-- For someone else -->
          <div class="flex flex-col gap-4 p-4">
            <label class="flex cursor-pointer items-center gap-2">
              <input class="sr-only" name="bookingFor" type="radio" value="someone_else" :checked="form.step2.shouldShowSomeoneElseForm" @input="form.step2.shouldShowSomeoneElseForm = true" />
              <span
                class="box-border inline-block h-4 w-4 min-w-4 rounded-full border border-solid"
                :class="[!form.step2.shouldShowSomeoneElseForm ? 'border-gray2' : 'border-black1', form.step2.shouldShowSomeoneElseForm ? 'bg-black1' : 'bg-white']"
              />
              <span :class="form.step2.shouldShowSomeoneElseForm ? 'font-semibold' : ''">For someone else</span>
            </label>
            <template v-if="form.step2.shouldShowSomeoneElseForm">
              <div class="flex flex-col gap-4 pl-6">
                <p class="text-sm font-semibold md:text-base">The lead participant's info</p>
                <div class="flex flex-col gap-4">
                  <div class="flex justify-between gap-[2%]">
                    <WrappedTextInput
                      v-model="form.step2.participantFirstName"
                      aria-label="participantFirstName"
                      autocomplete="section-participant given-name"
                      class="w-full"
                      label="First name"
                      placeholder="Taro"
                      :error="errorMessages.participantFirstName"
                      :lang="'en'"
                    />
                    <WrappedTextInput
                      v-model="form.step2.participantLastName"
                      aria-label="participantLastName"
                      autocomplete="section-participant family-name"
                      class="w-full"
                      label="Last name"
                      placeholder="Wabunka"
                      :error="errorMessages.participantLastName"
                      :lang="'en'"
                    />
                  </div>
                  <WrappedTextInput
                    v-model="form.step2.forSomeoneElseEmail"
                    aria-label="forSomeoneElseEmail"
                    autocomplete="section-participant email"
                    label="Email address"
                    placeholder="participant@example.com"
                    type="email"
                    :error="errorMessages.forSomeoneElseEmail"
                    :lang="'en'"
                  />
                  <WrappedTextInput
                    v-model="form.step2.forSomeoneElsePhoneNumber"
                    aria-label="forSomeoneElsePhoneNumber"
                    autocomplete="section-participant tel"
                    label="Phone number  (WhatsApp preferred)"
                    placeholder="+81 90-1234-5678"
                    type="tel"
                    :error="errorMessages.forSomeoneElsePhoneNumber"
                    :lang="'en'"
                  />
                  <MoleculesWabunkaWrappedComboBox
                    v-model="form.step2.forSomeoneElseNationality"
                    aria-label="forSomeoneElseNationality"
                    label="Nationality"
                    :error="errorMessages.forSomeoneElseNationality"
                    :lang="'en'"
                    :options="options"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </FormInputWrapper>

      <div class="flex-raw flex gap-x-1">
        <!-- name 必須: autocomplete="off" は Chrome の住所系オートフィルに無視されるため、name で field signature を変えて過去のクラウド予測（電話番号と誤学習）との紐付きを切る -->
        <WrappedTextInput
          v-model="codeName"
          aria-label="codeName"
          autocomplete="off"
          class="flex w-full flex-col"
          label="Apply Code (if you have one)"
          name="appliedCode"
          placeholder="Enter your code"
          :error="errorMessages.codeName"
          :lang="'en'"
          :required="false"
          @blur="codeName == '' && removeCode()"
        />
        <div class="pt-[26px]">
          <AtomsPrimaryButton class="w-1/4" data-testid="code-redeem-button" :disabled="codeName == '' || isSending" :on-click="() => applyCode(codeName)">{{
            !isSending ? 'Redeem' : 'Processing...'
          }}</AtomsPrimaryButton>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import AtomsPrimaryButton from '@/components/atoms/PrimaryButton/PrimaryButton.vue';
import MoleculesWabunkaWrappedComboBox from '@/components/molecules/wabunka/WrappedComboBox/WrappedComboBox.vue';
import { useCountry } from '@/composables/utils/useCountry';
import type { Option } from '@/models/option';
import { WrappedTextInput, FormInputWrapper } from '@/wabunka-src/common/components';
import { useGlobalWabunkaReservationForm } from '@/wabunka-src/common/features/Reservation/composables/forms/useGlobalWabunkaReservationForm';

interface Props {
  getErrorMessage: (s: string) => string;
  applyCode: (codeName: string) => Promise<void>;
  removeCode: () => void;
}

const props = defineProps<Props>();

const errorMessages = computed(() => ({
  firstName: props.getErrorMessage('step2.firstName'),
  lastName: props.getErrorMessage('step2.lastName'),
  email: props.getErrorMessage('step2.email'),
  forMyselfPhoneNumber: props.getErrorMessage('step2.forMyselfPhoneNumber'),
  forMyselfNationality: props.getErrorMessage('step2.forMyselfNationality'),
  participantFirstName: props.getErrorMessage('step2.participantFirstName'),
  participantLastName: props.getErrorMessage('step2.participantLastName'),
  forSomeoneElseEmail: props.getErrorMessage('step2.forSomeoneElseEmail'),
  forSomeoneElsePhoneNumber: props.getErrorMessage('step2.forSomeoneElsePhoneNumber'),
  forSomeoneElseNationality: props.getErrorMessage('step2.forSomeoneElseNationality'),
  codeName: props.getErrorMessage('step2.code.codeName'),
}));

const { form, isSending } = useGlobalWabunkaReservationForm();

// フォームの値を変えてしまうと料金計算が発火してしまうので、入力値を別途管理し、適用ボタン押下後に料金計算が発火するようにする
const codeName = ref('');

const options = ref<Option[]>([]);
onMounted(async () => {
  options.value = useCountry();
});
</script>
